import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect, useCallback } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const LOGO = "/assets/logo_transparent.png";

const FALLBACK_SLIDES = [
  { id: "f1", title: "Residencias de Autor", subtitle: "Casas que trascienden lo ordinario. Diseño, confort y exclusividad en cada detalle.", image_url: "https://images.unsplash.com/photo-1728722104881-773da8f4b7ea?w=1600&q=90", active: true },
  { id: "f2", title: "Edificios Corporativos", subtitle: "Espacios de trabajo que inspiran productividad y prestigio.", image_url: "https://images.unsplash.com/photo-1758411898734-18560c61c505?w=1600&q=90", active: true },
  { id: "f3", title: "Condominios Premium", subtitle: "Amenidades de clase mundial que redefinen el lujo.", image_url: "https://images.unsplash.com/photo-1776176569663-eeee5ffe3708?w=1600&q=90", active: true },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.5], [0.4, 0.75]);

  const [slides, setSlides] = useState(FALLBACK_SLIDES);
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    api.getCarousel().then(res => {
      const active = (res.data || []).filter(s => s.active);
      if (active.length > 0) setSlides(active);
    }).catch(() => {});
  }, []);

  const goTo = useCallback((idx) => {
    setCurrent(idx);
  }, []);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, slides.length, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, slides.length, goTo]);

  useEffect(() => {
    timerRef.current = setInterval(next, 6000);
    return () => clearInterval(timerRef.current);
  }, [next]);

  const resetTimer = (fn) => () => {
    clearInterval(timerRef.current);
    fn();
    timerRef.current = setInterval(next, 6000);
  };

  const slide = slides[current];

  const scrollToContact = () => {
    document.querySelector("#contacto")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="hero" ref={ref} className="relative h-screen overflow-hidden" data-testid="hero-section">
      {/* Background — Apple-style crossfade with Ken Burns */}
      <motion.div className="absolute inset-0" style={{ y }}>
        <AnimatePresence mode="sync">
          <motion.div
            key={slide.id + current}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0"
          >
            <motion.img
              src={slide.image_url}
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover will-change-transform"
              initial={{ scale: 1.08 }}
              animate={{ scale: 1 }}
              transition={{ duration: 8, ease: "linear" }}
              onError={(e) => { e.target.onerror = null; e.target.src = "https://images.unsplash.com/photo-1728722104881-773da8f4b7ea?w=1600&q=90"; }}
            />
          </motion.div>
        </AnimatePresence>
        <motion.div className="absolute inset-0 bg-black" style={{ opacity: overlayOpacity }} />
        {/* Cinematic vignette */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.55) 100%)" }} />
      </motion.div>

      {/* Subtle grain */}
      <div className="absolute inset-0 z-[2] opacity-[0.025] pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />

      {/* Decorative lines */}
      <div className="absolute inset-0 z-[3] pointer-events-none hidden lg:block">
        <motion.div initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="absolute left-[15%] top-0 w-px h-full bg-white/[0.04] origin-top" />
        <motion.div initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="absolute right-[25%] top-0 w-px h-full bg-white/[0.04] origin-top" />
      </div>

      {/* Content */}
      <motion.div style={{ opacity }}
        className="relative z-10 h-full flex flex-col justify-between px-6 md:px-12 lg:px-24 pt-28 pb-12 md:pb-20 max-w-[1800px] mx-auto">
        
        {/* Top — logo */}
        <motion.div initial={{ opacity: 0, y: -15 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: "fit-content" }} className="relative">
          <div className="absolute inset-0 z-10 pointer-events-none" style={{ mixBlendMode: "screen" }}>
            <div style={{
              background: "linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.06) 44%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 56%, transparent 62%)",
              backgroundSize: "250% 100%", animation: "shimmerSweep 6s ease-in-out infinite", position: "absolute", inset: 0,
            }} />
          </div>
          <motion.div animate={{ opacity: [0.2, 0.4, 0.2], scale: [1, 1.03, 1] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -inset-8 blur-3xl"
            style={{ background: "radial-gradient(ellipse at center, rgba(200,200,220,0.08) 0%, transparent 65%)" }} />
          <motion.img src={LOGO} alt="Merza Soluciones" className="h-16 md:h-24 relative"
            animate={{ y: [0, -2, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }} />
        </motion.div>

        {/* Center — Dynamic text — Apple-style fade */}
        <div className="flex-1 flex items-end pb-8">
          <div className="w-full">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.4 }}
              className="text-white/35 text-xs tracking-[0.4em] uppercase mb-6 md:mb-8">
              Arquitectura & Construccion Premium
            </motion.p>

            <div className="overflow-hidden min-h-[180px] md:min-h-[240px] lg:min-h-[280px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={slide.id + "text" + current}
                  initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -12, filter: "blur(4px)" }}
                  transition={{
                    duration: 0.8,
                    ease: [0.22, 1, 0.36, 1],
                    opacity: { duration: 0.6 },
                    filter: { duration: 0.5 },
                  }}
                >
                  <h1 className="text-5xl md:text-7xl lg:text-[6.5rem] xl:text-[7.5rem] font-medium text-white tracking-tight leading-[0.92] max-w-5xl"
                    style={{ fontFamily: "Outfit, sans-serif" }} data-testid="hero-title">
                    {slide.title}
                  </h1>
                  <p className="text-white/45 text-base md:text-lg lg:text-xl mt-4 md:mt-6 max-w-2xl font-light leading-relaxed tracking-wide"
                    data-testid="hero-subtitle">
                    {slide.subtitle}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* CTA buttons */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-4">
              <button data-testid="hero-cta-button" onClick={scrollToContact}
                className="group inline-flex items-center gap-3 bg-white/95 backdrop-blur-sm text-black px-8 py-4 rounded-full text-sm font-medium hover:bg-white hover:shadow-[0_8px_32px_rgba(255,255,255,0.12)] transition-all duration-500 active:scale-[0.98] w-fit">
                Iniciar proyecto
                <ArrowRight size={16} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform duration-300" />
              </button>
              <button onClick={() => document.querySelector("#proyectos")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 text-white/60 hover:text-white/90 px-8 py-4 text-sm font-light tracking-wide transition-all duration-500 w-fit group"
                data-testid="hero-projects-button">
                <span className="h-px w-6 bg-white/20 group-hover:w-10 group-hover:bg-white/40 transition-all duration-500" />
                Ver proyectos
              </button>
            </motion.div>
          </div>
        </div>

        {/* Bottom — controls */}
        <div className="flex items-center justify-between">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.5, duration: 1 }}
            className="flex items-center gap-3">
            <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
              className="w-5 h-8 rounded-full border border-white/15 flex items-start justify-center pt-1.5">
              <div className="w-1 h-1.5 rounded-full bg-white/40" />
            </motion.div>
            <span className="text-white/20 text-xs tracking-widest uppercase">Scroll</span>
          </motion.div>

          {/* Slide indicators — Apple style minimal */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 1 }}
            className="flex items-center gap-5">
            <div className="flex items-center gap-2.5">
              {slides.map((_, i) => (
                <button key={i} onClick={resetTimer(() => goTo(i))}
                  data-testid={`carousel-dot-${i}`}
                  className="relative h-8 flex items-center group">
                  <div className={`rounded-full transition-all duration-700 ease-out ${
                    i === current 
                      ? "w-8 h-[2px] bg-white/90" 
                      : "w-2 h-2 bg-white/20 group-hover:bg-white/35"
                  }`} />
                </button>
              ))}
            </div>

            {/* Arrows — minimal Apple style */}
            <div className="flex gap-1.5">
              <button onClick={resetTimer(prev)} data-testid="carousel-prev"
                className="w-9 h-9 rounded-full flex items-center justify-center text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-all duration-300 backdrop-blur-sm">
                <ChevronLeft size={15} strokeWidth={1.5} />
              </button>
              <button onClick={resetTimer(next)} data-testid="carousel-next"
                className="w-9 h-9 rounded-full flex items-center justify-center text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-all duration-300 backdrop-blur-sm">
                <ChevronRight size={15} strokeWidth={1.5} />
              </button>
            </div>

            {/* Counter — ultra minimal */}
            <div className="text-white/20 text-[11px] font-light tracking-[0.2em] hidden md:block" style={{ fontFamily: "Outfit, sans-serif" }}>
              <span className="text-white/60">{String(current + 1).padStart(2, "0")}</span>
              <span className="mx-1.5">—</span>
              <span>{String(slides.length).padStart(2, "0")}</span>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
